import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, Mail, Lock, Building } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ full_name: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setError('Invalid invitation link.'); setLoading(false); return; }
    api.get(`/api/organization/invite/${token}`).then(res => {
      setInvite(res.data);
      setForm(f => ({ ...f, email: res.data.email }));
      if (res.data.is_existing) {
        navigate(`/login?invite_token=${token}`, { replace: true });
      } else {
        navigate(`/register?invite_token=${token}`, { replace: true });
      }
    }).catch(e => {
      setError(e.response?.data?.detail || 'Invitation not found or expired.');
    }).finally(() => setLoading(false));
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/api/auth/register', {
        email: invite.email,
        password: form.password,
        full_name: form.full_name || invite.email.split('@')[0],
        invite_token: token,
      });
      toast.success('Account created! Please verify your email.');
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)' }}><div className="loader"></div></div>;

  if (error && !invite) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)', padding: 20 }}>
      <div style={{ textAlign: 'center', background: 'var(--bg-secondary)', padding: 40, borderRadius: 20, maxWidth: 420 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
        <h2 style={{ color: 'var(--text-primary)', margin: '0 0 8px' }}>Invalid Invitation</h2>
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>{error}</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 460, background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 24, padding: 36, boxShadow: 'var(--shadow-xl)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--accent-glow)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <Users size={28} color="var(--accent-primary)" />
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Join {invite?.organization_name || 'your team'}</h1>
          <p style={{ color: 'var(--text-secondary)', margin: '8px 0 0' }}>
            You've been invited as <strong style={{ color: 'var(--accent-amber)' }}>{invite?.role || 'SDR'}</strong>
          </p>
          {invite?.is_existing && (
            <p style={{ margin: '16px 0 0', padding: '12px', background: 'var(--info-bg)', border: '1px solid var(--info)', borderRadius: 8, fontSize: 12, color: 'var(--info)' }}>
              You already have an iLeadX account. Please log in to accept the invitation.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Email</label>
            <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', borderRadius: 10, padding: '0 12px', opacity: 0.7 }}>
              <Mail size={16} color="var(--text-tertiary)" />
              <input disabled value={invite?.email || ''} style={{ flex: 1, padding: '12px', border: 'none', background: 'none', color: 'var(--text-primary)', fontSize: 14, outline: 'none', fontFamily: 'inherit' }} />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Full Name</label>
            <div style={{ display: 'flex', alignItems: 'center', background: 'var(--input-bg)', border: '1px solid var(--border-primary)', borderRadius: 10, padding: '0 12px' }}>
              <Building size={16} color="var(--text-tertiary)" />
              <input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Your full name" required style={{ flex: 1, padding: '12px', border: 'none', background: 'none', color: 'var(--text-primary)', fontSize: 14, outline: 'none', fontFamily: 'inherit' }} />
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Password</label>
            <div style={{ display: 'flex', alignItems: 'center', background: 'var(--input-bg)', border: '1px solid var(--border-primary)', borderRadius: 10, padding: '0 12px' }}>
              <Lock size={16} color="var(--text-tertiary)" />
              <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Minimum 8 characters" required style={{ flex: 1, padding: '12px', border: 'none', background: 'none', color: 'var(--text-primary)', fontSize: 14, outline: 'none', fontFamily: 'inherit' }} />
            </div>
          </div>

          {error && <p style={{ color: 'var(--error)', fontSize: 13, marginBottom: 12 }}>{error}</p>}

          {invite?.is_existing ? (
            <button onClick={async () => {
              setSubmitting(true);
              try {
                await api.post(`/api/organization/invite/${token}/accept`);
                toast.success('Invitation accepted. Please log in.');
                navigate('/login');
              } catch (e) {
                setError(e.response?.data?.detail || 'Failed to accept invitation');
              } finally { setSubmitting(false); }
            }} disabled={submitting} className="auth-button auth-button-primary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: 48, fontSize: 15 }}>
              {submitting ? 'Accepting...' : 'Accept Invitation'}
            </button>
          ) : (
            <button type="submit" disabled={submitting} className="auth-button auth-button-primary" style={{ width: '100%', height: 48, fontSize: 15 }}>
              {submitting ? 'Creating Account...' : 'Accept Invitation'}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
